import React, { useState, useEffect, useCallback } from 'react';
import authFetch from '../utils/authFetch';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const GoalsCard = ({ isMobile }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSetGoals, setShowSetGoals] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [goalsForm, setGoalsForm] = useState({
    yearly_letters_sent: 0,
    yearly_pridobivanje: 0,
    yearly_sold_net: 0,
  });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [saving, setSaving] = useState(false);

  const fetchProgress = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/api/goals/progress`);
      if (!res.ok) throw new Error('Failed to load goals progress');
      const data = await res.json();
      setProgress(data);
      setGoalsForm({
        yearly_letters_sent: data.yearly.letters_sent.goal,
        yearly_pridobivanje: data.yearly.pridobivanje.goal,
        yearly_sold_net: data.yearly.sold_net.goal,
      });
    } catch (err) {
      console.error(err);
      setError('Could not load goals.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const handleSaveGoals = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/goals`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year: selectedYear, ...goalsForm }),
      });
      if (!res.ok) throw new Error('Failed to save goals');
      setShowSetGoals(false);
      fetchProgress();
    } catch (err) {
      console.error(err);
      alert('Failed to save goals.');
    } finally {
      setSaving(false);
    }
  };

  const fetchGoalsForYear = useCallback(async (year) => {
    try {
      const res = await authFetch(`${API_BASE}/api/goals/year/${year}`);
      if (!res.ok) throw new Error('Failed to load goals for year');
      const data = await res.json();
      setGoalsForm({
        yearly_letters_sent: data.yearly_letters_sent || 0,
        yearly_pridobivanje: data.yearly_pridobivanje || 0,
        yearly_sold_net: data.yearly_sold_net || 0,
      });
    } catch (err) {
      console.error(err);
      setGoalsForm({ yearly_letters_sent: 0, yearly_pridobivanje: 0, yearly_sold_net: 0 });
    }
  }, []);

  const openSetGoals = (year) => {
    setSelectedYear(year || new Date().getFullYear());
    fetchGoalsForYear(year || new Date().getFullYear());
    setShowSetGoals(true);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    fetchGoalsForYear(year);
  };

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/goals/history`);
      if (!res.ok) throw new Error('Failed to load history');
      const data = await res.json();
      setHistoryData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const openHistory = () => {
    setShowHistory(true);
    fetchHistory();
  };

  const formatValue = (val, isCurrency) => {
    if (isCurrency) return `€${Number(val).toLocaleString('sl-SI', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    return val;
  };

  const getProgressColor = (value, goal) => {
    if (goal <= 0) return '#64748b';
    const pct = value / goal;
    if (pct >= 1) return '#16a34a';
    if (pct >= 0.5) return '#d97706';
    return '#dc2626';
  };

  const getProgressPct = (value, goal) => {
    if (goal <= 0) return 0;
    return Math.min((value / goal) * 100, 100);
  };

  const ProgressRow = ({ label, value, goal, isCurrency }) => (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
        <span style={{ fontSize: '13px', color: '#334155', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: '13px', fontWeight: 600, color: getProgressColor(value, goal) }}>
          {formatValue(value, isCurrency)} / {formatValue(goal, isCurrency)}
        </span>
      </div>
      <div style={{ height: '7px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${getProgressPct(value, goal)}%`,
          backgroundColor: getProgressColor(value, goal),
          borderRadius: '4px',
          transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  );

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '24px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px' }}>
          🎯 Cilji
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={openHistory}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              backgroundColor: '#f1f5f9',
              color: '#334155',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            History
          </button>
          <button
            onClick={() => openSetGoals()}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              backgroundColor: '#f1f5f9',
              color: '#334155',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Set Goals
          </button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', color: '#64748b', padding: '20px 0' }}>Loading…</div>}
      {error && <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '10px' }}>{error}</div>}

      {progress && !loading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '16px',
        }}>
          {/* Monthly */}
          <div style={{
            backgroundColor: '#f8fafc',
            borderRadius: '10px',
            padding: '16px',
            border: '1px solid #e2e8f0',
          }}>
            <h4 style={{ margin: '0 0 14px 0', color: '#1e293b', fontSize: '15px' }}>
              📅 Mesečni cilji <span style={{ fontWeight: 400, color: '#64748b', fontSize: '12px' }}>
                ({new Date().toLocaleString('sl-SI', { month: 'long', year: 'numeric' })})
              </span>
            </h4>
            <ProgressRow
              label="Letters sent"
              value={progress.monthly.letters_sent.value}
              goal={progress.monthly.letters_sent.goal}
              isCurrency={false}
            />
            <ProgressRow
              label="Pridobivanje v prodajo"
              value={progress.monthly.pridobivanje.value}
              goal={progress.monthly.pridobivanje.goal}
              isCurrency={false}
            />
            <ProgressRow
              label="Sold (neto provizije)"
              value={progress.monthly.sold_net.value}
              goal={progress.monthly.sold_net.goal}
              isCurrency={true}
            />
          </div>

          {/* Yearly */}
          <div style={{
            backgroundColor: '#f8fafc',
            borderRadius: '10px',
            padding: '16px',
            border: '1px solid #e2e8f0',
          }}>
            <h4 style={{ margin: '0 0 14px 0', color: '#1e293b', fontSize: '15px' }}>
              📆 Letni cilji <span style={{ fontWeight: 400, color: '#64748b', fontSize: '12px' }}>
                ({new Date().getFullYear()})
              </span>
            </h4>
            <ProgressRow
              label="Letters sent"
              value={progress.yearly.letters_sent.value}
              goal={progress.yearly.letters_sent.goal}
              isCurrency={false}
            />
            <ProgressRow
              label="Pridobivanje v prodajo"
              value={progress.yearly.pridobivanje.value}
              goal={progress.yearly.pridobivanje.goal}
              isCurrency={false}
            />
            <ProgressRow
              label="Sold (neto provizije)"
              value={progress.yearly.sold_net.value}
              goal={progress.yearly.sold_net.goal}
              isCurrency={true}
            />
          </div>
        </div>
      )}

      {/* Set Goals Modal */}
      {showSetGoals && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000,
        }} onClick={() => setShowSetGoals(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: '440px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            padding: '28px',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 20px 0', color: '#1e293b', fontSize: '18px' }}>Set Goals</h3>

            {/* Year selector buttons */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {[2026, 2027, 2028, 2029, 2030].map((year) => (
                <button
                  key={year}
                  onClick={() => handleYearChange(year)}
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: selectedYear === year ? 700 : 500,
                    backgroundColor: selectedYear === year ? '#2563eb' : '#f1f5f9',
                    color: selectedYear === year ? 'white' : '#475569',
                    border: selectedYear === year ? '2px solid #2563eb' : '1px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {year}
                </button>
              ))}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#475569', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Yearly Goals ({selectedYear})
              </h4>
              <GoalInput label="Letters sent" value={goalsForm.yearly_letters_sent}
                onChange={(v) => setGoalsForm({ ...goalsForm, yearly_letters_sent: v })} />
              <GoalInput label="Pridobivanje v prodajo" value={goalsForm.yearly_pridobivanje}
                onChange={(v) => setGoalsForm({ ...goalsForm, yearly_pridobivanje: v })} />
              <GoalInput label="Sold – neto provizije (€)" value={goalsForm.yearly_sold_net}
                onChange={(v) => setGoalsForm({ ...goalsForm, yearly_sold_net: v })} />
            </div>

            <div style={{
              marginBottom: '24px',
              backgroundColor: '#f8fafc',
              borderRadius: '10px',
              padding: '14px 16px',
              border: '1px solid #e2e8f0',
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#64748b', fontSize: '13px', fontWeight: 500 }}>
                Monthly (auto-calculated: yearly ÷ 12)
              </h4>
              <div style={{ fontSize: '13px', color: '#334155', lineHeight: '1.8' }}>
                <div>Letters sent: <strong>{Math.round((goalsForm.yearly_letters_sent / 12) * 100) / 100}</strong> / month</div>
                <div>Pridobivanje: <strong>{Math.round((goalsForm.yearly_pridobivanje / 12) * 100) / 100}</strong> / month</div>
                <div>Sold neto: <strong>€{Math.round(goalsForm.yearly_sold_net / 12).toLocaleString('sl-SI')}</strong> / month</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSetGoals(false)}
                style={{
                  padding: '9px 18px', fontSize: '14px',
                  backgroundColor: '#f1f5f9', color: '#475569',
                  border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGoals}
                disabled={saving}
                style={{
                  padding: '9px 18px', fontSize: '14px',
                  backgroundColor: '#2563eb', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  fontWeight: 600, opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Save Goals'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10000,
        }} onClick={() => setShowHistory(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: '720px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            padding: '28px',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px' }}>📊 Goals History</h3>
              <button
                onClick={() => setShowHistory(false)}
                style={{
                  padding: '6px 14px', fontSize: '13px',
                  backgroundColor: '#f1f5f9', color: '#64748b',
                  border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            {historyLoading && <div style={{ textAlign: 'center', color: '#64748b', padding: '30px 0' }}>Loading…</div>}

            {!historyLoading && historyData.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                      <th style={thStyle}>Month</th>
                      <th style={thStyle}>Letters sent</th>
                      <th style={thStyle}>Pridobivanje</th>
                      <th style={thStyle}>Sold (neto)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((row) => {
                      const monthLabel = new Date(row.month + '-01').toLocaleString('sl-SI', { month: 'short', year: 'numeric' });
                      return (
                        <tr key={row.month} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={tdStyle}>
                            <span style={{ fontWeight: 600, color: '#1e293b' }}>{monthLabel}</span>
                          </td>
                          <td style={tdStyle}>
                            <HistoryCell value={row.letters_sent.value} goal={row.letters_sent.goal} />
                          </td>
                          <td style={tdStyle}>
                            <HistoryCell value={row.pridobivanje.value} goal={row.pridobivanje.goal} />
                          </td>
                          <td style={tdStyle}>
                            <HistoryCell value={row.sold_net.value} goal={row.sold_net.goal} isCurrency />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!historyLoading && historyData.length === 0 && (
              <div style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0' }}>No history data available.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const HistoryCell = ({ value, goal, isCurrency }) => {
  const met = goal > 0 && value >= goal;
  const fmt = (v) => isCurrency ? `€${Number(v).toLocaleString('sl-SI', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : v;
  const color = goal <= 0 ? '#64748b' : met ? '#16a34a' : '#dc2626';
  return (
    <span style={{ color, fontWeight: 500 }}>
      {fmt(value)} / {fmt(goal)}
      {goal > 0 && (
        <span style={{ marginLeft: '6px', fontSize: '11px' }}>{met ? '✓' : '✗'}</span>
      )}
    </span>
  );
};

const thStyle = { textAlign: 'left', padding: '10px 12px', color: '#64748b', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', whiteSpace: 'nowrap' };

const GoalInput = ({ label, value, onChange }) => (
  <div style={{ marginBottom: '12px' }}>
    <label style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>{label}</label>
    <input
      type="number"
      min="0"
      value={value}
      onChange={(e) => onChange(Number(e.target.value) || 0)}
      style={{
        width: '100%',
        padding: '9px 12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '14px',
        boxSizing: 'border-box',
        outline: 'none',
      }}
    />
  </div>
);

export default GoalsCard;
